import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Box, Grid, Button, Paper, Stack, Alert,
  TextField, FormControl, InputLabel, Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import api_endpoints from "../../../api";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import { useApiClientMutation } from "../../../hooks/useApiClient";
import { useSuccessAlert } from "../../../context/SuccessAlertContext/SuccessAlertContext";

/* ---- ErrorBoundary wrapper ---- */
class ModalBoundary extends React.Component {
  state = { hasError: false, err: null };
  static getDerivedStateFromError(err) {
    return { hasError: true, err };
  }
  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error">
          {this.state.err?.message || "Something went wrong in Add Payment Method"}
        </Alert>
      );
    }
    return this.props.children;
  }
}

/* ---- script helpers ---- */
function cloverSdkUrl() {
  const env = (process.env.REACT_APP_CLOVER_ENV || "sandbox").toLowerCase();
  return env === "production"
    ? "https://checkout.clover.com/sdk.js"
    : "https://checkout.sandbox.dev.clover.com/sdk.js";
}
// Forte: env decided by script host; sandbox has /api/js/v1
function forteJsUrl() {
  const env = (process.env.REACT_APP_FORTE_ENV || "sandbox").toLowerCase();
  return env === "production"
    ? "https://api.forte.net/js/v1"
    : "https://sandbox.forte.net/api/js/v1";
}
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some((s) => s.src === src)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

/* ---- Clover  ---- */
function useClover(organizationId) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const cloverRef = useRef(null);
  const cardEls = useRef({});
  const createdEls = useRef([]); // for cleanup

  useEffect(() => {
    if (!organizationId) { setReady(false); return; }
    let cancelled = false;
    (async () => {
      try {
        await loadScript(cloverSdkUrl());
        if (!window.Clover) throw new Error("Clover SDK missing");
        const { apiAccessKey, merchantId } =
          await api_endpoints.getCloverPakms({ organization_id: organizationId });

        const options = merchantId ? { merchantId } : undefined;
        const clover = new window.Clover(apiAccessKey, options);
        cloverRef.current = clover;

        const elements = clover.elements();
        const styles = {
          body: { fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif", fontSize: "14px" },
          input: { fontSize: "14px" }
        };

        cardEls.current.number = elements.create("CARD_NUMBER", styles);
        cardEls.current.date   = elements.create("CARD_DATE", styles);
        cardEls.current.cvv    = elements.create("CARD_CVV", styles);
        cardEls.current.postal = elements.create("CARD_POSTAL_CODE", styles);
        createdEls.current = Object.values(cardEls.current);
        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) setError(e);
      }
    })();
    return () => {
      cancelled = true;
      // Unmount/destroy if the modal unmounts to avoid "already mounted" errors on reopen
      createdEls.current.forEach(el => { try { el?.unmount?.(); } catch {} });
      createdEls.current = [];
      cardEls.current = {};
      cloverRef.current = null;
    };
  }, [organizationId]);

  return { ready, error, cloverRef, cardEls };
}

/* ---- Forte attribute callbacks (global names) ---- */
function useForteAttributeCallbacks({ onSuccess, onError }) {
  useEffect(() => {
    window.__forteSuccess = (resp) => onSuccess?.(resp);
    window.__forteError = (resp) => onError?.(resp);
    return () => {
      delete window.__forteSuccess;
      delete window.__forteError;
    };
  }, [onSuccess, onError]);
}

/* ---- Styled Forte text field (real <input> so forte can read) ---- */
function ForteTextField({ id, label, forteData, placeholder = "", type = "text" }) {
  return (
    <TextField
      id={id}
      label={label}
      fullWidth
      size="small"
      variant="outlined"
      type={type}
      InputLabelProps={{ shrink: true }}
      inputProps={{ "forte-data": forteData, placeholder, autoComplete: "off" }}
      sx={{
        "& .MuiOutlinedInput-root": { borderRadius: 2 },
        "& .MuiOutlinedInput-input::placeholder": { opacity: 0.6 },
      }}
    />
  );
}

/* ---- Styled wrapper for Clover Elements so they look like MUI inputs ---- */
/*  Height fix lives here: explicit 40px height + force iframes to fill */
function CloverField({ label, fieldId }) {
  return (
    <FormControl fullWidth>
      <InputLabel shrink sx={{ mb: 0.5, lineHeight: 1 }}>{label}</InputLabel>
      <Box
        id={fieldId}
        sx={(theme) => ({
          mt: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 1.25,
          height: 40,                // <-- fixed, compact height
          display: "flex",
          alignItems: "center",
          transition: "border-color 120ms ease, box-shadow 120ms ease",
          "&:focus-within": {
            borderColor: "primary.main",
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`
          },
          // make the hosted iframes/containers fill the wrapper height
          "& iframe": { width: "100%", height: "100%" },
          "& > div": { width: "100%", height: "100%" }
        })}
      />
    </FormControl>
  );
}

/* ---- Component ---- */
export function AddPaymentMethodModal({ onClose, onSave, organizationId, donorId }) {
  const [tab, setTab] = useState(0); // 0=ACH, 1=Card
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingInstrument, setPendingInstrument] = useState(null);

  const [cardholderName, setCardholderName] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const { t } = useCustomTranslation("Recurring Payment");
  const {
    setShowSuccessAlert,
    setMessageSuccessAlert,
    setShowWarningAlert,
    setMessageWarningAlert,
  } = useSuccessAlert();

  const pushSuccessToast = useCallback(
    (message) => {
      setMessageSuccessAlert(message);
      setShowSuccessAlert(true);
    },
    [setMessageSuccessAlert, setShowSuccessAlert]
  );

  const pushWarningToast = useCallback(
    (message) => {
      setMessageWarningAlert(message);
      setShowWarningAlert(true);
    },
    [setMessageWarningAlert, setShowWarningAlert]
  );

  const translateOr = useCallback(
    (key, fallback) => {
      const value = t(key);
      return value && value !== key ? value : fallback;
    },
    [t]
  );

  useEffect(() => {
    if (!organizationId || !donorId) {
      setErr(t("organization_donor_required"));
    }
  }, [organizationId, donorId, t]);

  /* Clover */
  const clover = useClover(organizationId);
  const mountIds = {
    number: "clover-card-number",
    date: "clover-card-date",
    cvv: "clover-card-cvv",
    postal: "clover-card-postal",
  };
  const cloverMountedRef = useRef(false);
  const pendingInstrumentRef = useRef(null);

  useEffect(() => {
    if (tab !== 1 || !clover.ready || cloverMountedRef.current) return;
    const { number, date, cvv, postal } = clover.cardEls.current || {};
    try {
      if (!number || !date || !cvv || !postal) throw new Error(t("clover_fields_not_ready"));
      number.mount(`#${mountIds.number}`);
      date.mount(`#${mountIds.date}`);
      cvv.mount(`#${mountIds.cvv}`);
      postal.mount(`#${mountIds.postal}`);
      cloverMountedRef.current = true;
    } catch (e) {
      setErr(e?.message || t("card_ingest_failed"));
    }
  }, [tab, clover.ready, t, mountIds.number, mountIds.date, mountIds.cvv, mountIds.postal]);

  /* Forte loader */
  const [forteReady, setForteReady] = useState(false);
  const [forteErr, setForteErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        await loadScript(forteJsUrl());
        if (!window.forte || typeof window.forte !== "object") {
          throw new Error(t("forte_global_missing"));
        }
        setForteReady(true);
      } catch (e) {
        setForteErr(String(e?.message || e));
      }
    })();
  }, [t]);

  useForteAttributeCallbacks({
    onSuccess: async (resp) => {
      try {
        if (!organizationId || !donorId) {
          setErr(t("organization_donor_required"));
          return;
        }
        setSaving(true);

        const { customer_token } = await api_endpoints.forteFindOrCreateCustomer({
          organization_id: organizationId,
          donor_id: donorId,
        });

        const one_time_token = resp?.one_time_token || resp?.onetime_token;
        if (!one_time_token) throw new Error(t("forte_missing_ott"));

        const payload = await api_endpoints.tokenizeForteAch({
          organization_id: organizationId,
          donor_id: donorId,
          one_time_token,
          forte_customer_token: customer_token,
          last4: resp?.last_4 || "",
          account_type: resp?.account_type || "",
          bank_name: bankName || "",
          account_holder: accountHolderName || "",
        });

        onSave?.({ provider: "forte_ach", ...payload });
        pushSuccessToast(translateOr("ach_ingest_success", "Bank account saved successfully."));
        setSaving(false);
        setTimeout(() => onClose?.(), 1500);
      } catch (e) {
        setSaving(false);
        setErr(e?.response?.data?.message || e?.message || t("ach_ingest_failed"));
        pushWarningToast(e?.response?.data?.message || e?.message || t("ach_ingest_failed"));
      }
    },
    onError: (resp) => {
      setErr(resp?.response_description || t("forte_tokenization_failed"));
      pushWarningToast(resp?.response_description || t("forte_tokenization_failed"));
    },
  });

  const sendPaymentLinkMutation = useApiClientMutation(
    ({ donor_id, organization_id, instrument_type }) =>
      api_endpoints.sendDonorPaymentMethodLink({ donor_id, organization_id, instrument_type }),
    [],
    (data) => {
      const instrument = pendingInstrumentRef.current;
      pendingInstrumentRef.current = null;
      setPendingInstrument(null);
      setErr("");
      const translated = data?.message ? t(data.message) : undefined;
      const fallback = instrument === "ach"
        ? translateOr("bank_link_sent", "Bank payment link sent.")
        : translateOr("card_link_sent", "Card payment link sent.");
      const message = translated && translated !== data?.message ? translated : fallback;
      pushSuccessToast(message);
    },
    (error) => {
      pendingInstrumentRef.current = null;
      setPendingInstrument(null);
      setErr(error?.response?.data?.message || t("payment_link_failed"));
      pushWarningToast(error?.response?.data?.message || t("payment_link_failed"));
    }
  );
  const { mutate: triggerPaymentLink, isPending: isSendingPaymentLink } = sendPaymentLinkMutation;

  /* Save card via Clover */
  const handleSaveCard = useCallback(async () => {
    try {
      if (!organizationId || !donorId) {
        setErr(t("organization_donor_required"));
        return;
      }
      if (!clover.ready || !cloverMountedRef.current) {
        setErr(t("clover_fields_not_ready"));
        return;
      }
      setSaving(true);

      const clv = clover.cloverRef.current;
      if (!clv) throw new Error(t("clover_fields_not_ready"));
      const result = await clv.createToken();
      if (result?.errors) {
        const msg = Object.values(result.errors).join(" ");
        throw new Error(msg || t("card_tokenization_failed"));
      }
      const token = result?.token; // Clover returns a clv_ token
      if (!token) throw new Error(t("missing_clover_token"));

      const c = result?.card || {};
      const resp = await api_endpoints.tokenizeCloverCard({
        organization_id: organizationId,
        donor_id: donorId,
        token,
        brand: c?.brand || "",
        first6: c?.first6 || "",
        last4: c?.last4 || "",
        exp_month: c?.exp_month || "",
        exp_year: c?.exp_year || "",
        cardholderName: cardholderName || "",
        billingAddress: billingAddress || "",
      });

      onSave?.({ provider: "clover_ecom", ...resp });
      pushSuccessToast(t("card_ingest_success"));
      setSaving(false);
      setTimeout(() => onClose?.(), 1500);
    } catch (e) {
      setSaving(false);
      setErr(e?.response?.data?.message || e?.message || t("card_ingest_failed"));
      pushWarningToast(e?.response?.data?.message || e?.message || t("card_ingest_failed"));
    }
  }, [organizationId, donorId, cardholderName, billingAddress, onSave, onClose, clover.ready, t, pushSuccessToast, pushWarningToast]);

  const handleSendPaymentLink = useCallback(
    (instrument) => {
      if (!organizationId || !donorId) {
        setErr(t("organization_donor_required"));
        return;
      }
      pendingInstrumentRef.current = instrument;
      setPendingInstrument(instrument);
      setErr("");
      triggerPaymentLink({ donor_id: donorId, organization_id: organizationId, instrument_type: instrument });
    },
    [organizationId, donorId, t, triggerPaymentLink]
  );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t("add_payment_method_modal")}</DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
        <Tab label={t("banking_eft") || "Banking EFT"} />
        <Tab label={t("credit_card") || "Credit Card"} />
      </Tabs>

      <DialogContent dividers>
        {!!err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {!!clover.error && <Alert severity="error" sx={{ mb: 2 }}>
          {clover.error.message || String(clover.error)}
        </Alert>}

        {tab === 0 ? (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("forte_section_title")}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleSendPaymentLink("ach")}
                  disabled={!organizationId || !donorId || (isSendingPaymentLink && pendingInstrument === "ach")}
                >
                  {t("send_bank_link")}
                </Button>
              </Box>

              {/* Let Forte.js handle submit; no preventDefault */}
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("account_holder_name") || "Account holder name"}
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={accountHolderName}
                      onChange={(e) => setAccountHolderName(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("bank_name_optional")}
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <ForteTextField id="forte-routing" label={t("routing_number")} forteData="routing_number" placeholder="123456789" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <ForteTextField id="forte-account" label={t("account_number")} forteData="account_number" placeholder="000123456789" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={t("account_type")}
                      fullWidth
                      size="small"
                      select
                      defaultValue="c"
                      variant="outlined"
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ "forte-data": "account_type" }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    >
                      <option value="c">{t("checking")}</option>
                      <option value="s">{t("savings")}</option>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving || !forteReady}
                      sx={{ borderRadius: 2, px: 2.5, textTransform: "none", fontWeight: 600 }}
                      {...{
                        "forte-api-login-id": process.env.REACT_APP_FORTE_API_LOGIN_ID || "",
                        "forte-callback-success": "__forteSuccess",
                        "forte-callback-error": "__forteError",
                      }}
                    >
                      {t("tokenize_bank")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t("clover_section_title")}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleSendPaymentLink("card")}
                  disabled={!organizationId || !donorId || (isSendingPaymentLink && pendingInstrument === "card")}
                >
                  {t("send_card_link")}
                </Button>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("cardholder_name")}
                    fullWidth
                    size="small"
                    value={cardholderName}
                    onChange={(e) => setCardholderName(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("billing_address")}
                    fullWidth
                    size="small"
                    value={billingAddress}
                    onChange={(e) => setBillingAddress(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Clover elements styled to match MUI text fields */}
                <Grid item xs={12}>
                  <CloverField label={t("card_number")} fieldId={mountIds.number} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CloverField label={t("expiry_mm_yy")} fieldId={mountIds.date} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CloverField label={t("cvc")} fieldId={mountIds.cvv} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CloverField label={t("postal_code")} fieldId={mountIds.postal} />
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button color="error" onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          {t("cancel")}
        </Button>
        {tab === 1 ? (
          <Button
            variant="contained"
            onClick={handleSaveCard}
            disabled={saving || !clover.ready || !cloverMountedRef.current}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {t("save_card")}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
